import { useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAppDispatch, useAppSelector } from '../redux/helper';
import {
  setOwnerAuthError,
  setOwnerAuthLoading,
  setOwnerIdentifier,
  setOwnerTokens,
} from '../redux/backoffice/ownerAuthSlice';
import { axiosinstance } from '../axiosClient';
import { normalizeEmail } from '../utils/libs';
import { ownerLogin } from '../api/backofficeAuth';
import { getBackofficeCredentials } from '../services/SecureCredentials';

const OWNER_ACCESS_TOKEN_KEY = 'owner_access_token';
const OWNER_REFRESH_TOKEN_KEY = 'owner_refresh_token';

const useBackofficeOwnerAuth = () => {
  const dispatch = useAppDispatch();
  const owner = useAppSelector(s => s.backofficeOwnerAuth);

  const autoLoginOwner = useCallback(async () => {
    try {
      // Idempotent: if we already have tokens in memory, don't re-login.
      if (owner?.accessToken && owner?.refreshToken) {
        return {
          status: 'existing' as const,
          accessToken: owner.accessToken,
          refreshToken: owner.refreshToken,
        };
      }

      // Prefer cached tokens (fast path, no network).
      const [cachedAccess, cachedRefresh] = await Promise.all([
        AsyncStorage.getItem(OWNER_ACCESS_TOKEN_KEY),
        AsyncStorage.getItem(OWNER_REFRESH_TOKEN_KEY),
      ]);
      if (cachedAccess && cachedRefresh) {
        dispatch(
          setOwnerTokens({
            accessToken: cachedAccess,
            refreshToken: cachedRefresh,
          }),
        );
        return {
          status: 'cached' as const,
          accessToken: cachedAccess,
          refreshToken: cachedRefresh,
        };
      }

      dispatch(setOwnerAuthLoading(true));
      dispatch(setOwnerAuthError(null));

      // Pull credentials from the same helpers Browser uses
      const creds = await getBackofficeCredentials();
      if (!creds?.email || !creds?.password) {
        throw new Error('Missing owner credentials');
      }

      const emailOrUsername = normalizeEmail(creds.email);
      const password = creds.password.trim();
      dispatch(setOwnerIdentifier(emailOrUsername));

      const res = await ownerLogin(
        axiosinstance as any,
        emailOrUsername,
        password,
      );
      if (res?.status === 200) {
        const access = String(res?.accessToken || '');
        const refresh = String(res?.refreshToken || '');
        dispatch(
          setOwnerTokens({ accessToken: access, refreshToken: refresh }),
        );
        // Tokens are still cached in AsyncStorage by the ownerAuth slice usage elsewhere.
        // Keeping as-is since these are tokens (not raw passwords).
        await AsyncStorage.setItem(OWNER_ACCESS_TOKEN_KEY, access);
        await AsyncStorage.setItem(OWNER_REFRESH_TOKEN_KEY, refresh);
        return {
          status: 'success' as const,
          accessToken: access,
          refreshToken: refresh,
        };
      }
      throw new Error(res?.message || 'Owner login failed');
    } catch (e: any) {
      dispatch(setOwnerAuthError(e?.message || 'Owner login error'));

      return { status: 'error' as const, message: e?.message };
    } finally {
      dispatch(setOwnerAuthLoading(false));
    }
  }, [dispatch, owner?.accessToken, owner?.refreshToken]);

  return { owner, autoLoginOwner } as const;
};

export default useBackofficeOwnerAuth;
